import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { PriorityHigh } from '@mui/icons-material';
import { Button, Checkbox } from '@mui/material';

import ConfirmationDialog from 'commons/components/ConfirmationDialog/ConfirmationDialog.component';
import * as S from 'commons/components/Table/Table.styles';
import { OrderListRowAction, OrderStatusEnum } from 'commons/enums';
import { PickingTask } from 'commons/types';
import {
  DefaultSortProps,
  RowTitleProps,
  SortOrder,
} from 'commons/types/listPageData.interface';
import { DataTableProps } from 'commons/types/orderItemData.interface';
import { TableActionRow, DataTable } from 'pages/dashboard/commons/components';
import { syncAwbAction } from 'redux-stores/actions';
import { RootReducerInterface } from 'redux-stores/reducers';
import { AppDispatch } from 'redux-stores/store';

import {
  OpenBulkDialogConfig,
  OpenDialogConfig,
} from '../configs/open-dialog.config';
import {
  IOpenBulkDialogData,
  IOpenDialogData,
} from '../type/open-dialog-state.interface';

import BulkActionBtnMenu from './BulkActionBtnMenu.component';
import PackingRowActionBtn from './PackingRowActionBtn.component';
import PickingRowActionBtn from './PickingRowActionBtn.component';

const rowTitles: RowTitleProps[] = [
  { title: 'PO Number' },
  { title: 'Order Type' },
  { title: 'Order Date', sortField: 'queued_at' },
  { title: 'Status' },
  { title: 'Picker/Packer' },
  { title: 'Priority', sortField: 'priority' },
  { title: 'Flag Reason' },
];

const defaultSort: DefaultSortProps = {
  sortField: 'queued_at',
  sortOrder: SortOrder.DESC,
};

const Row: React.FC<{
  data: PickingTask;
  onCheckboxClick: (poNumber: string) => void;
  checked: boolean;
  selectedLocation?: number;
  index: number;
}> = ({ data, onCheckboxClick, checked, selectedLocation, index }) => {
  const dispatch = useDispatch<AppDispatch>();
  const [open, setOpen] = useState<boolean>(false);
  const [openDialogData, setOpenDialogData] = useState<IOpenDialogData | null>(
    null,
  );

  // assign invoice and pharmacy data to picking_task inside packing_task
  // FIXME: to improve data from API, because extended picking task dont have invoice and pharmacy object
  const clonedPackingTask = structuredClone(data.packing_task);
  if (clonedPackingTask) {
    clonedPackingTask.picking_task.invoice = data.invoice;
    clonedPackingTask.picking_task.pharmacy = data.pharmacy;
    clonedPackingTask.picking_task.verify_document_url =
      data.verify_document_url;
    clonedPackingTask.picking_task.order_verification_document =
      data.order_verification_document;
  }

  const handleOpenDialog = (name: string) => {
    setOpenDialogData(OpenDialogConfig[name.toLocaleLowerCase()]);
    setOpen(true);
  };

  const handleCloseDialog = () => {
    setOpenDialogData(null);
    setOpen(false);
  };

  return (
    <>
      <S.TableRow>
        <S.TableColumn>
          <Checkbox
            data-testid={`checkboxOrderListPONumber{${index + 1}}`}
            onClick={() => onCheckboxClick(data.po_number)}
            checked={checked}
          />
        </S.TableColumn>
        <S.TableColumn>
          {data.is_high_priority && <PriorityHigh />}
        </S.TableColumn>
        <S.TableColumn data-testid={`textOrderListPONumber{${index + 1}}`}>
          {data.po_number}
        </S.TableColumn>
        <S.TableColumn
          data-testid={`textOrderListPOType{${index + 1}}`}
          style={{ textTransform: 'capitalize' }}
        >
          {data.po_type}
        </S.TableColumn>
        <S.TableColumn data-testid={`textOrderListQueuedAt{${index + 1}}`}>
          {new Date(data.queued_at).toLocaleString('id-ID')}
        </S.TableColumn>
        <S.TableColumn
          style={{ textTransform: 'capitalize', paddingRight: '15px' }}
          data-testid={`textOrderListStatus{${index + 1}}`}
        >
          {data.status}
        </S.TableColumn>
        <S.TableColumn>
          <>
            <div data-testid={`textOrderListPicker{${index + 1}}`}>{`Picker: ${
              data.picker ? data.picker.split('@')[0] : '-'
            }`}</div>
            <div data-testid={`textOrderListPacker{${index + 1}}`}>
              {`Packer: ${
                data.packing_task?.packer
                  ? data.packing_task.packer.split('@')[0]
                  : '-'
              }`}
            </div>
          </>
        </S.TableColumn>
        <S.TableColumn data-testid={`textOrderListPriority{${index + 1}}`}>
          {data.priority}
        </S.TableColumn>
        <S.TableColumn data-testid={`textOrderListFlagReason{${index + 1}}`}>
          {data.reason}
        </S.TableColumn>
        <S.TableColumn style={{ textAlign: 'right' }}>
          {data.status === OrderStatusEnum.UPDATE_FAILED ? (
            <div
              style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}
            >
              <Button
                data-testid={`buttonOrderListRetry{${index + 1}}`}
                onClick={() => handleOpenDialog('retry')}
                variant="outlined"
                sx={{ width: 87 }}
              >
                Retry
              </Button>
              <Button
                onClick={() =>
                  dispatch(
                    syncAwbAction({
                      poNumbers: [data.po_number],
                    }),
                  )
                }
                variant="outlined"
                sx={{ minWidth: 87, paddingLeft: '4px', paddingRight: '4px' }}
              >
                Sync AWB Info
              </Button>
            </div>
          ) : [OrderStatusEnum.QUEUE, OrderStatusEnum.PENDING].includes(
              data.status,
            ) ? (
            <PickingRowActionBtn
              data-testid={`buttonOrderListPicking{${index + 1}}`}
              pickingTask={data}
              selectedLocation={selectedLocation}
            />
          ) : [OrderStatusEnum.PACKING, OrderStatusEnum.PACKED].includes(
              data.status,
            ) && clonedPackingTask ? (
            <PackingRowActionBtn
              data-testid={`buttonOrderListPacking{${index + 1}}`}
              orderType={data.po_type}
              packingTask={clonedPackingTask}
            />
          ) : (
            <Button
              onClick={() =>
                dispatch(
                  syncAwbAction({
                    poNumbers: [data.po_number],
                  }),
                )
              }
              variant="outlined"
              sx={{ minWidth: 87, paddingLeft: '4px', paddingRight: '4px' }}
            >
              Sync AWB Info
            </Button>
          )}
        </S.TableColumn>
      </S.TableRow>
      <ConfirmationDialog
        title={openDialogData?.title || ''}
        content={openDialogData?.content(data.po_number) || ''}
        onClose={() => handleCloseDialog()}
        openModal={open}
        onConfirm={() =>
          openDialogData?.onConfirm(dispatch, data.po_number, selectedLocation)
        }
      />
    </>
  );
};

const OrderTable: React.FC<DataTableProps> = (params): React.ReactNode => {
  const { data = [] }: DataTableProps = params;

  const [checkAll, setCheckAll] = useState<boolean>(false);
  const [checkedOrders, setCheckedOrders] = useState<string[]>([]);

  const selectedOrders = data.filter((task) =>
    checkedOrders.includes(task.po_number),
  );

  const { selectedLocation } = useSelector(
    (state: RootReducerInterface) => state.dashboard,
  );

  const dispatch = useDispatch<AppDispatch>();
  const selectedPoNumbers = selectedOrders.map((order) => order.po_number);
  const [open, setOpen] = useState<boolean>(false);
  const [openDialogData, setOpenDialogData] =
    useState<IOpenBulkDialogData | null>(null);
  const handleCheckOrder = (poNumber: string): void => {
    if (checkedOrders.includes(poNumber)) {
      // remove poNumber from checkedOrders
      checkedOrders.splice(checkedOrders.indexOf(poNumber), 1);
      setCheckedOrders([...checkedOrders]);
    } else {
      // insert po number to checkedOrders
      setCheckedOrders([...checkedOrders, poNumber]);
    }
  };

  const handleCheckAll = (): void => {
    if (checkAll) {
      // remove all check list
      setCheckedOrders([]);
    } else {
      // add all po number to checkedOrders
      setCheckedOrders(data.map((pickTask) => pickTask.po_number));
    }
    setCheckAll(!checkAll);
  };

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const openMenu = Boolean(anchorEl);

  const handleClickMenu = (
    event: React.MouseEvent<HTMLButtonElement>,
  ): void => {
    setAnchorEl(event.currentTarget);
  };
  const handleCloseMenu = (): void => {
    setAnchorEl(null);
  };

  const handleOpenDialog = (name: string) => {
    if (name === OrderListRowAction.SYNC_AWB) {
      dispatch(
        syncAwbAction({
          poNumbers: selectedPoNumbers,
        }),
      );
      return;
    }
    setOpenDialogData(OpenBulkDialogConfig[name]);
    setOpen(true);
  };

  const handleCloseDialog = () => {
    setOpenDialogData(null);
    setOpen(false);
  };

  return (
    <>
      <TableActionRow
        dataName="order"
        selectedData={selectedOrders}
        anchorEl={anchorEl}
        openMenu={openMenu}
        handleClickMenu={handleClickMenu}
      >
        <BulkActionBtnMenu
          selectedOrders={selectedOrders}
          handleCloseMenu={handleCloseMenu}
          handleOpen={handleOpenDialog}
        />
      </TableActionRow>
      <DataTable
        handleCheckAll={handleCheckAll}
        checkAll={checkAll}
        rowTitles={rowTitles}
        defaultSort={defaultSort}
        extraColumn
      >
        <S.TableBody>
          {data.map((item, index) => (
            <Row
              data={item}
              key={item.po_number}
              onCheckboxClick={handleCheckOrder}
              checked={checkedOrders.includes(item.po_number)}
              selectedLocation={selectedLocation?.warehouse_id}
              index={index}
            />
          ))}
        </S.TableBody>
      </DataTable>
      {data.length > 0 && (
        <TableActionRow
          dataName="order"
          selectedData={selectedOrders}
          anchorEl={anchorEl}
          openMenu={openMenu}
          handleClickMenu={handleClickMenu}
        >
          <BulkActionBtnMenu
            selectedOrders={selectedOrders}
            handleCloseMenu={handleCloseMenu}
            handleOpen={handleOpenDialog}
          />
        </TableActionRow>
      )}
      <ConfirmationDialog
        title={openDialogData?.title || ''}
        content={openDialogData?.content(selectedPoNumbers) || ''}
        onClose={() => handleCloseDialog()}
        openModal={open}
        onConfirm={() =>
          openDialogData?.onConfirm(
            dispatch,
            selectedPoNumbers,
            selectedLocation?.warehouse_id,
          )
        }
      />
    </>
  );
};

export default OrderTable;
